/*
  VBRNTT poster carousel: a horizontal row of slides ([data-slide]) that
  rotates only when there's something to rotate through.

  Modes, re-decided whenever the visible slides change (the city scroller
  fires "vbrntt:posters-filtered" after it filters) or the row resizes:
  - empty:    no visible slides. The whole carousel, heading included, hides.
  - static:   fewer than 3 visible slides, or too few to overflow the row.
              A still, left-aligned row: no arrows, no rotation.
  - carousel: 3+ slides that overflow. Arrows either side, and auto-rotation
              unless the visitor prefers reduced motion.

  Auto-rotation pauses while the pointer is over it, while focus is inside
  it, while it's off screen or the tab is hidden, and for good when the
  visitor presses Pause (WCAG 2.2.2: hover and focus alone aren't a way to
  stop motion). The control is a small icon button in the row's top-right
  corner (snippets/vbrntt-pause-button.liquid): data-paused swaps the icon,
  and its hidden label swaps between the pause and play labels. Arrow keys move it when focus is inside the row.

  Used by the city scroller (events) and the past-events section (home, events). Without
  JavaScript the row still scrolls sideways and nothing moves.
*/
(() => {
  if (customElements.get('vbrntt-poster-carousel')) return;

  const MIN_TO_ROTATE = 3;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class VbrnttPosterCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.prevButton = this.querySelector('[data-prev]');
      this.nextButton = this.querySelector('[data-next]');
      this.pauseButton = this.querySelector('[data-pause]');
      if (!this.track) return;

      this.interval = Math.max(3000, Number(this.dataset.interval) || 5000);
      this.userPaused = false;
      this.hovered = false;
      this.focused = false;
      this.onScreen = true;
      this.timer = null;

      this.update = this.update.bind(this);
      this.sync = this.sync.bind(this);
      this.onPrev = () => this.go(-1);
      this.onNext = () => this.go(1);
      this.onPause = this.onPause.bind(this);
      this.onEnter = () => { this.hovered = true; this.sync(); };
      this.onLeave = () => { this.hovered = false; this.sync(); };
      this.onFocusIn = () => { this.focused = true; this.sync(); };
      this.onFocusOut = (event) => { this.focused = this.contains(event.relatedTarget); this.sync(); };
      this.onKeyDown = this.onKeyDown.bind(this);

      if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
      if (this.pauseButton) this.pauseButton.addEventListener('click', this.onPause);
      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.track.addEventListener('keydown', this.onKeyDown);
      this.addEventListener('vbrntt:posters-filtered', this.update);
      reducedMotion.addEventListener('change', this.update);
      document.addEventListener('visibilitychange', this.sync);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.track);
      }
      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          this.onScreen = entries.some((entry) => entry.isIntersecting);
          this.sync();
        });
        this.intersectionObserver.observe(this);
      }

      this.update();
    }

    disconnectedCallback() {
      this.stop();
      reducedMotion.removeEventListener('change', this.update);
      document.removeEventListener('visibilitychange', this.sync);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
    }

    visibleSlides() {
      return Array.from(this.track.querySelectorAll('[data-slide]')).filter((slide) => !slide.hidden);
    }

    /* Picks the mode from what's visible now. */
    update() {
      const count = this.visibleSlides().length;
      this.hidden = count === 0;
      let mode = 'empty';
      if (count > 0) {
        const overflows = this.track.scrollWidth > this.track.clientWidth + 2;
        mode = count >= MIN_TO_ROTATE && overflows ? 'carousel' : 'static';
      }
      const changed = this.dataset.mode !== mode;
      this.dataset.mode = mode;

      const rotating = mode === 'carousel';
      if (this.prevButton) this.prevButton.hidden = !rotating;
      if (this.nextButton) this.nextButton.hidden = !rotating;
      if (this.pauseButton) this.pauseButton.hidden = !rotating || reducedMotion.matches;
      // A new filter starts at the first poster rather than mid-row.
      if (changed || !rotating) this.track.scrollTo({ left: 0, behavior: 'auto' });
      this.sync();
    }

    /* One slide's width plus the gap. */
    step() {
      const first = this.visibleSlides()[0];
      if (!first) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return first.getBoundingClientRect().width + gap;
    }

    /* Moves one slide; wraps from the end back to the start, and the reverse. */
    go(direction) {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const at = this.track.scrollLeft;
      let target;
      if (direction > 0) target = at >= max - 2 ? 0 : Math.min(max, at + this.step());
      else target = at <= 2 ? max : Math.max(0, at - this.step());
      this.track.scrollTo({ left: target, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    shouldRotate() {
      return (
        this.dataset.mode === 'carousel' &&
        !reducedMotion.matches &&
        !this.userPaused &&
        !this.hovered &&
        !this.focused &&
        this.onScreen &&
        document.visibilityState === 'visible'
      );
    }

    sync() {
      if (this.shouldRotate()) {
        if (!this.timer) this.timer = window.setInterval(() => this.go(1), this.interval);
      } else {
        this.stop();
      }
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    onPause() {
      this.userPaused = !this.userPaused;
      this.pauseButton.toggleAttribute('data-paused', this.userPaused);
      const label = this.userPaused ? this.dataset.playLabel : this.dataset.pauseLabel;
      const text = this.pauseButton.querySelector('[data-pause-text]');
      if (label && text) text.textContent = label;
      this.sync();
    }

    onKeyDown(event) {
      if (this.dataset.mode !== 'carousel') return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.go(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.go(-1);
      }
    }
  }

  customElements.define('vbrntt-poster-carousel', VbrnttPosterCarousel);
})();
